// RedQuickNamePrompt — what comes up the moment a UPC fails to resolve
// to ANY name. Two paths to fix it:
//
//   1. TAP-TO-PLACE (preferred) — tap one of your unpurchased
//      shopping-list items. The scan inherits that item's canonical
//      and pairs to the slot. One tap, done.
//   2. TYPE A NAME (fallback) — when the item isn't on your list (an
//      unplanned grab), type a quick identifier and the row goes
//      yellow until you bind a canonical at home.
//
// Hard rule (per user, this session): the cart list NEVER shows a UPC.
// Every red row gets either a tap-to-place pairing or a typed name
// here, or — if the user skips — the productName stays null and the
// Resolve screen surfaces it for binding.

import { useEffect, useRef } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { TIER_COLORS } from "./tierColors";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontFamily: FONT.detail,
  fontStyle: "italic",
  fontSize: 16,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${TIER_COLORS.red.border}`,
  borderRadius: RADIUS.md,
  outline: "none",
};

const saveBtnStyle = {
  flex: 1,
  padding: "10px 14px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.night,
  background: COLOR.leaf,
  border: "none",
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const skipBtnStyle = {
  padding: "10px 14px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  background: "transparent",
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const helpStyle = {
  fontFamily: FONT.sans,
  fontSize: 11,
  color: COLOR.muted,
  marginTop: 4,
};

const sectionLabelStyle = {
  fontFamily: FONT.mono,
  fontSize: 9,
  letterSpacing: "0.16em",
  color: TIER_COLORS.red.fg,
  textTransform: "uppercase",
  marginTop: 4,
};

const placeChipStyle = {
  padding: "8px 12px",
  fontFamily: FONT.detail,
  fontStyle: "italic",
  fontSize: 14,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.pill || 999,
  cursor: "pointer",
  whiteSpace: "nowrap",
  // Forms a tight grid; flex-shrink off so chips keep their width.
  flexShrink: 0,
  touchAction: "manipulation",
};

const dividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "2px 0",
};
const dividerLineStyle = {
  flex: 1,
  height: 1,
  background: COLOR.edge,
};
const dividerLabelStyle = {
  fontFamily: FONT.mono,
  fontSize: 9,
  letterSpacing: "0.18em",
  color: COLOR.muted,
};

export default function RedQuickNamePrompt({
  scanId,
  upc,
  shoppingList = [],
  onPlace,
  onSave,
  onSkip,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus on mount so the user can type immediately if they
    // prefer that over tap-to-place. iOS keyboard viewport is handled
    // at the meta-tag level (per memory).
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [scanId]);

  function handleSave() {
    const v = inputRef.current?.value?.trim();
    if (!v) return;
    onSave?.(scanId, v);
  }

  function handlePlace(listItem) {
    onPlace?.(scanId, listItem);
  }

  function handleKey(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onSkip?.(scanId);
    }
  }

  // Filter to unpurchased + uncovered list items (a list item already
  // paired to a different scan shouldn't show as a place target). The
  // parent passes the already-filtered list — we just guard against
  // stale entries here.
  const placeTargets = (shoppingList || []).filter((it) => it && !it.purchasedAt);
  const hasTargets = placeTargets.length > 0;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: 14,
      // Solid background — this prompt overlays the live BarcodeScanner
      // camera feed, and the previous TIER_COLORS.red.bg (10% opacity)
      // rendered as visually-transparent on top of the busy video.
      // Use the same cream paper as the rest of the app, with a
      // tinted red overlay so the "unresolved" tier signal still
      // reads clearly.
      background: COLOR.paper || "#FBF5E8",
      backgroundImage: `linear-gradient(${TIER_COLORS.red.bg}, ${TIER_COLORS.red.bg})`,
      border: `2px solid ${TIER_COLORS.red.border}`,
      borderRadius: RADIUS.md,
      boxShadow: "0 12px 32px rgba(20, 12, 4, 0.45), 0 4px 12px rgba(20, 12, 4, 0.30)",
      maxHeight: "70vh",
      overflowY: "auto",
    }}>
      <div style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        letterSpacing: "0.18em",
        color: TIER_COLORS.red.fg,
      }}>
        UNRESOLVED · UPC {upc?.slice?.(-6) || "????"}
      </div>
      <div style={{
        fontFamily: FONT.detail,
        fontStyle: "italic",
        fontSize: 18,
        color: COLOR.ink,
      }}>
        {hasTargets ? "What did you scan?" : "What is this?"}
      </div>

      {hasTargets && (
        <>
          <div style={sectionLabelStyle}>
            Tap one of your shopping list items
          </div>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}>
            {placeTargets.map((item) => (
              <button
                key={item.id}
                onClick={() => handlePlace(item)}
                style={placeChipStyle}
                className="mcm-focusable"
              >
                {item.name || "(unnamed)"}
              </button>
            ))}
          </div>

          <div style={dividerStyle}>
            <div style={dividerLineStyle} />
            <div style={dividerLabelStyle}>OR TYPE A NAME</div>
            <div style={dividerLineStyle} />
          </div>
        </>
      )}

      <input
        ref={inputRef}
        type="text"
        placeholder={
          hasTargets
            ? "or describe it (off-list grab, etc.)"
            : "e.g. Trader Joe's chickpea snack"
        }
        onKeyDown={handleKey}
        style={inputStyle}
      />
      <div style={helpStyle}>
        {hasTargets
          ? "Tap a list item to place this exactly. Type a name when it isn't on your list."
          : "Just enough so you'll know what it is at home. We'll bind a canonical later."}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={handleSave} style={saveBtnStyle}>Save</button>
        <button onClick={() => onSkip?.(scanId)} style={skipBtnStyle}>Skip</button>
      </div>
    </div>
  );
}
